/**
 * Active workspace (§5, §12.3).
 *
 * The id is persisted in a cookie and sent as `X-Workspace-Id` on every request. Switching
 * invalidates every server-state query, because nothing cached under the previous tenant is
 * valid for the new one — leaving even one stale list would show another workspace's data.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { api, setWorkspaceResolver } from '@/api/client'
import type { Workspace } from '@/stores/auth'

const COOKIE = 'nlocal_workspace'

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]!) : null
}

function writeCookie(name: string, value: string): void {
  // Session-scoped and same-site; the server is the authority, this is only a preference.
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=31536000; SameSite=Lax`
}

export const useWorkspaceStore = defineStore('workspace', () => {
  const workspaces = ref<Workspace[]>([])
  const currentId = ref<string | null>(readCookie(COOKIE))

  const current = computed(
    () => workspaces.value.find((w) => w.id === currentId.value) ?? workspaces.value[0] ?? null,
  )

  const role = computed(() => current.value?.role ?? null)

  /** §6.2 capability check, mirroring EnsureRole on the server. */
  const ROLES = ['owner', 'admin', 'developer', 'viewer'] as const

  function hasAtLeastRole(minimum: (typeof ROLES)[number]): boolean {
    const mine = ROLES.indexOf(role.value as (typeof ROLES)[number])
    const required = ROLES.indexOf(minimum)

    // Fail closed on an unknown role, exactly as the server does.
    return mine !== -1 && required !== -1 && mine <= required
  }

  function setWorkspaces(list: Workspace[]): void {
    workspaces.value = list

    // A remembered id that is no longer accessible (removed from the workspace, or the
    // cookie survived a different login) must not keep being sent.
    if (currentId.value !== null && !list.some((w) => w.id === currentId.value)) {
      currentId.value = list[0]?.id ?? null
    }

    if (currentId.value === null && list.length > 0) {
      currentId.value = list[0]!.id
    }

    if (currentId.value !== null) {
      writeCookie(COOKIE, currentId.value)
    }
  }

  function switchTo(id: string): void {
    if (id === currentId.value) return

    currentId.value = id
    writeCookie(COOKIE, id)
  }

  async function refresh(): Promise<void> {
    const { data, error } = await api.GET('/v1/workspaces')
    if (error) return

    setWorkspaces((data as { data: Workspace[] }).data ?? [])
  }

  // Wire the client's workspace header to this store.
  setWorkspaceResolver(() => currentId.value)

  return {
    workspaces,
    currentId,
    current,
    role,
    hasAtLeastRole,
    setWorkspaces,
    switchTo,
    refresh,
  }
})
